import { FormEvent, useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Plus, RefreshCw, Smartphone } from 'lucide-react';
import { getApiErrorMessage, portalApi } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import type { ActivationTokenResponse, MerchantLocation, PosTerminal } from '../../api/types';

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function TerminalsPage() {
  const [locations, setLocations] = useState<MerchantLocation[]>([]);
  const [terminals, setTerminals] = useState<PosTerminal[]>([]);
  const [locationName, setLocationName] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [activation, setActivation] = useState<ActivationTokenResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const [locRes, termRes] = await Promise.all([portalApi.listLocations(), portalApi.listTerminals()]);
      setLocations(locRes.locations ?? []);
      setTerminals(termRes.terminals ?? []);
      setErr('');
    } catch (ex) {
      setErr(getApiErrorMessage(ex));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const copy = (text: string) => void navigator.clipboard.writeText(text);

  const onCreateToken = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    setLoading(true);
    try {
      const body = selectedLocationId
        ? { location_id: selectedLocationId, default_fiat_currency: currency }
        : { location_name: locationName.trim(), default_fiat_currency: currency };
      if (!selectedLocationId && !locationName.trim()) {
        setErr('Enter a location name or select an existing location');
        setLoading(false);
        return;
      }
      const res = await portalApi.createTerminalActivationToken(body);
      setActivation(res);
      setMsg('Activation code created — valid 15 minutes');
      void load();
    } catch (ex) {
      setErr(getApiErrorMessage(ex));
    } finally {
      setLoading(false);
    }
  };

  const expiresIn = activation?.expires_at
    ? Math.max(0, Math.floor((new Date(activation.expires_at).getTime() - Date.now()) / 1000))
    : 0;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2">
            <Smartphone size={26} />
            POS terminals
          </h2>
          <p className="text-sm text-onyx-muted mt-1">
            Pair cashier tablets without sharing your owner login. Each terminal is scoped to one store location.
          </p>
        </div>
        <Button variant="ghost" type="button" onClick={() => void load()}>
          <RefreshCw size={16} className="inline mr-2" />
          Refresh
        </Button>
      </div>

      {err ? <p className="text-sm text-onyx-danger">{err}</p> : null}
      {msg ? <p className="text-sm text-onyx-success">{msg}</p> : null}

      <Card title="Add terminal">
        <form onSubmit={(e) => void onCreateToken(e)} className="space-y-4">
          {locations.length > 0 ? (
            <div>
              <label className="text-xs text-onyx-muted block mb-1">Existing location</label>
              <select
                className="w-full rounded-xl bg-onyx-elevated border border-onyx-border px-3 py-2 text-sm text-onyx-text"
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
              >
                <option value="">— New location —</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.default_fiat_currency})
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {!selectedLocationId ? (
            <div>
              <label className="text-xs text-onyx-muted block mb-1">Store / location name</label>
              <Input
                placeholder="Dubai Mall — Coffee Shop"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
              />
            </div>
          ) : null}

          <div>
            <label className="text-xs text-onyx-muted block mb-1">Default fiat currency</label>
            <Input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              placeholder="AED"
            />
          </div>

          <Button type="submit" disabled={loading}>
            <Plus size={16} className="inline mr-2" />
            {loading ? 'Generating…' : 'Generate activation code'}
          </Button>
        </form>
      </Card>

      {activation ? (
        <Card title="Activation — scan on POS tablet">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="flex flex-col items-center gap-4">
              <div className="bg-white p-4 rounded-2xl">
                <QRCodeSVG value={activation.qr_payload} size={220} level="M" />
              </div>
              <p className="text-xs text-onyx-muted text-center">
                Open Onyx Pay POS → Activate → Scan QR
              </p>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-onyx-muted mb-1">Activation token</p>
                <div className="flex items-center gap-2 font-mono text-lg tracking-widest">
                  {activation.token}
                  <button type="button" onClick={() => copy(activation.token)} className="text-onyx-gold">
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-onyx-muted mb-1">Expires</p>
                <p>
                  {formatTs(activation.expires_at)}
                  {expiresIn > 0 ? (
                    <span className="text-onyx-muted">
                      {' '}
                      ({Math.floor(expiresIn / 60)}m {expiresIn % 60}s left)
                    </span>
                  ) : (
                    <span className="text-onyx-warn text-xs ml-2">Expired</span>
                  )}
                </p>
              </div>
              <p className="text-xs text-onyx-muted leading-relaxed">
                Single-use code. The tablet stores a terminal secret after pairing. Unpair from terminal settings
                (Admin PIN).
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      <Card title="Paired terminals">
        {terminals.length === 0 ? (
          <p className="text-sm text-onyx-muted">No terminals paired yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-onyx-muted border-b border-onyx-border">
                  <th className="py-2 pr-4">Label</th>
                  <th className="py-2 pr-4">Location</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Activated</th>
                  <th className="py-2">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {terminals.map((row) => (
                  <tr key={row.id} className="border-b border-onyx-border/50">
                    <td className="py-3 pr-4 font-mono text-xs">{row.label}</td>
                    <td className="py-3 pr-4">
                      {row.location_name}
                      <span className="text-onyx-muted text-xs ml-1">({row.default_fiat_currency})</span>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge status={row.status} />
                    </td>
                    <td className="py-3 pr-4 text-onyx-muted">{formatTs(row.activated_at)}</td>
                    <td className="py-3 text-onyx-muted">{formatTs(row.last_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
